import React, { Fragment, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  ColumnDef,
  flexRender,
  Row,
  getPaginationRowModel,
  ColumnFiltersState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import {
  TableHeader,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '@/components/ui/table.tsx';
import { MultiDeviceMetricsTablePagination } from './multi-device-metrics-table-pagination.tsx';
import { MultiDeviceMetricsTableToolbar } from './multi-device-metrics-table-toolbar.tsx';
import { cn } from '@/utils/utils.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { PlantDeviceFields } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-devices-overview.ts';
import { tableColumns } from './multi-device-metrics-table.columns.tsx';

const EMPTY_DATA: PlantDeviceFields[] = [];

type TableProps<TData> = {
  data: TData[] | undefined;
  columns: ColumnDef<TData>[];
  RenderSubComponent: (props: { row: Row<TData>; plantId: string }) => React.ReactElement;
  getRowCanExpand: (row: Row<TData>) => boolean;
  plantId: string;
};

export const MultiDeviceMetricsTable = ({
  data,
  columns,
  RenderSubComponent,
  getRowCanExpand,
  plantId,
  isLoading,
}: TableProps<PlantDeviceFields> & { isLoading: boolean | undefined }) => {
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable<PlantDeviceFields>({
    data: data ?? EMPTY_DATA,
    columns,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      columnFilters,
    },
    getRowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),

    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const headers = useMemo(() => {
    return tableColumns()?.map((c) => c.label);
  }, []);

  return (
    <div className="flex h-full flex-col justify-between p-0 sm:p-2">
      <div className="min-h-[350px]">
        <div className="h-4" />
        <MultiDeviceMetricsTableToolbar table={table} />
        <div className="h-4" />
        <Table>
          <TableHeader className="hidden sm:[display:table-header-group]">
            {table.getHeaderGroups()?.map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers?.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width: header.getSize() !== 150 ? header.getSize() : undefined,
                        textWrap: 'nowrap',
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows?.map((row) => {
                return (
                  <Fragment key={row.id}>
                    <TableRow className="flex h-[380px] flex-col py-2 text-base sm:[display:table-row] sm:h-min sm:py-0 sm:text-sm">
                      {row.getVisibleCells()?.map((cell, i) => {
                        return (
                          <TableCell
                            key={cell.id}
                            data-cell={headers[i] ? `${headers[i]}` : ''}
                            className={cn(
                              'before:text-muted-foreground block before:block before:text-sm before:font-semibold before:content-[attr(data-cell)] sm:[display:table-cell] sm:before:content-[attr(data-no-cell)]',
                              i === 0 && 'order-4'
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow className="hover:bg-white">
                        <TableCell colSpan={row.getVisibleCells().length}>
                          <RenderSubComponent row={row} plantId={plantId} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 p-0 text-center">
                  {isLoading ? (
                    <div className="flex w-full items-center justify-center py-20">
                      <LoadingSpinner size={50} />
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[240px] w-full items-center justify-center rounded-md bg-gray-50 text-base">
                      <span className="text-muted-foreground">No parameters found</span>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div>
        <div className="h-4" />
        <MultiDeviceMetricsTablePagination table={table} />
      </div>
    </div>
  );
};
